import { AnyAction } from 'redux';
import { PaginatedResponse } from '@synergy/api';

import {
    COMPLETE_PURCHASE_ORDER,
    DELETE_PURCHASE_ORDER,
    DUPLICATE_PURCHASE_ORDER,
    RECEIVE_PURCHASE_ORDER,
    RECEIVE_PURCHASE_ORDERS,
    REQUEST_PURCHASE_ORDER,
    REQUEST_PURCHASE_ORDER_END,
    REQUEST_PURCHASE_ORDER_START,
    RESET_PURCHASE_ORDERS_STATE,
    RESET_PURCHASE_ORDER_STATE,
    UPDATE_PURCHASE_ORDER_ALERT,
} from './purchase-order-actions';
import { PurchaseOrder } from './models/purchaseOrder';

export interface MappedPurchaseOrders {
    [id: number]: PurchaseOrder;
}

export interface PurchaseOrderReducerState {
    entity?: PurchaseOrder;
    byId: MappedPurchaseOrders;
    flat: PaginatedResponse<PurchaseOrder>;
    requesting: boolean;
}

const initialPurchaseOrderReducerState: PurchaseOrderReducerState = {
    entity: undefined,
    byId: {},
    flat: { limit: 10, page: 0, total: 0, items: [] },
    requesting: false,
};

export const mapPurchaseOrdersById = (purchaseOrders: PurchaseOrder[]): MappedPurchaseOrders => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return purchaseOrders.reduce((accumulator: {}, purchaseOrder) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accumulator as any)[purchaseOrder.id] = purchaseOrder;

        return accumulator;
    }, {});
};

export const purchaseOrdersReducer = (
    state: PurchaseOrderReducerState = initialPurchaseOrderReducerState,
    action: AnyAction
): PurchaseOrderReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case RECEIVE_PURCHASE_ORDERS: {
            return {
                ...state,
                byId: mapPurchaseOrdersById(action['purchaseOrders'].items),
                flat: action['purchaseOrders'],
            };
        }

        case RECEIVE_PURCHASE_ORDER: {
            // Add or update current purchase-order array
            const paginatedPurchaseOrders = { ...state.flat };
            const purchaseOrders = [...paginatedPurchaseOrders.items];
            const existingPurchaseOrder = purchaseOrders.find(
                purchaseOrder => purchaseOrder.id === action['entity'].id
            );
            if (existingPurchaseOrder) {
                const index = purchaseOrders.indexOf(existingPurchaseOrder);
                purchaseOrders[index] = action['entity'];
            } else {
                purchaseOrders.unshift(action['entity']);
                // remove last one if surpassing page size
                if (purchaseOrders.length > paginatedPurchaseOrders.limit) {
                    purchaseOrders.pop();
                }
            }
            paginatedPurchaseOrders.items = purchaseOrders;

            const entity = (action['entity'] ?? state.entity) as PurchaseOrder;

            return {
                ...state,
                flat: paginatedPurchaseOrders,
                byId: mapPurchaseOrdersById(purchaseOrders),
                entity,
                requesting: false,
            };
        }

        case RESET_PURCHASE_ORDERS_STATE: {
            return {
                ...state,
                byId: initialPurchaseOrderReducerState.byId,
                flat: initialPurchaseOrderReducerState.flat,
            };
        }

        case RESET_PURCHASE_ORDER_STATE: {
            return {
                ...state,
                entity: initialPurchaseOrderReducerState.entity,
            };
        }

        case DELETE_PURCHASE_ORDER: {
            const paginatedPurchaseOrders = { ...state.flat };
            const purchaseOrders = paginatedPurchaseOrders.items.filter(
                purchaseOrder => purchaseOrder.id !== rest['purchaseOrderId']
            );
            paginatedPurchaseOrders.items = purchaseOrders;
            paginatedPurchaseOrders.total--;

            return {
                ...state,
                flat: paginatedPurchaseOrders,
                byId: mapPurchaseOrdersById(purchaseOrders),
                entity: initialPurchaseOrderReducerState.entity,
            };
        }

        case COMPLETE_PURCHASE_ORDER: {
            if (!state.entity) {
                return state;
            }

            const entity = (rest['entity'] ?? state.entity) as PurchaseOrder;

            return {
                ...state,
                entity: {
                    ...state.entity,
                    ...entity,
                    status: rest['status'],
                },
            };
        }

        case UPDATE_PURCHASE_ORDER_ALERT: {
            if (!state.entity) {
                return state;
            }

            return {
                ...state,
                entity: {
                    ...state.entity,
                    receiptAlert: rest['alert'],
                },
            };
        }

        case REQUEST_PURCHASE_ORDER:
        case DUPLICATE_PURCHASE_ORDER:
        case REQUEST_PURCHASE_ORDER_START: {
            return {
                ...state,
                requesting: true,
            };
        }

        case REQUEST_PURCHASE_ORDER_END: {
            return {
                ...state,
                requesting: false,
            };
        }

        default: {
            return state;
        }
    }
};
